// useTimeEntries.js - Complete version with split support + bill_rate_role
import { useState, useEffect, useRef } from 'react'
import { supabase } from '../lib/supabase'
import { useTenant } from '../contexts/TenantContext'
import { useAuth } from '../contexts/AuthContext'

export const useTimeEntries = (filters = {}) => {
  const [timeEntries, setTimeEntries] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const { currentTenant, userRole } = useTenant()
  const { user } = useAuth()
  
  // Use refs to prevent infinite loops
  const lastFiltersRef = useRef()
  const isFetchingRef = useRef(false)
  const abortControllerRef = useRef()

  // Only fetch when filters actually change (deep comparison)
  useEffect(() => {
    if (!currentTenant || !user) {
      setTimeEntries([])
      setLoading(false)
      return
    }

    const filtersString = JSON.stringify(filters)
    const lastFiltersString = JSON.stringify(lastFiltersRef.current)
    
    // Only fetch if filters changed or first time
    if (filtersString !== lastFiltersString && !isFetchingRef.current) {
      lastFiltersRef.current = filters
      fetchTimeEntries()
    }
  }, [currentTenant?.id, user?.id, userRole, JSON.stringify(filters)])

  const fetchTimeEntries = async () => {
    if (isFetchingRef.current) {
      console.log('Already fetching, skipping...')
      return
    }

    if (!currentTenant || !user) {
      setTimeEntries([])
      setLoading(false)
      return
    }

    try {
      isFetchingRef.current = true
      setLoading(true)
      setError(null)

      // Cancel previous request if exists
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }

      abortControllerRef.current = new AbortController()
      const signal = abortControllerRef.current.signal

      console.log('Fetching time entries for role:', userRole)

      let data, fetchError

      // Try enhanced view first
      try {
        console.log('Trying enhanced view...')
        
        let enhancedQuery = supabase
          .from('time_entries_with_splits')
          .select('*')
          .eq('tenant_id', currentTenant.id)
          .abortSignal(signal)

        // Apply role-based filtering
        if (userRole === 'user') {
          enhancedQuery = enhancedQuery.eq('user_id', user.id)
        }

        // Apply filters
        if (filters.status && filters.status !== 'all') {
          if (filters.status === 'not_approved') {
            // Handle "Pending Review" filter - includes submitted and in_review
            enhancedQuery = enhancedQuery.in('status', ['submitted', 'in_review'])
          } else {
            enhancedQuery = enhancedQuery.eq('status', filters.status)
          }
        }

        if (filters.clientId && filters.clientId !== 'all') {
          enhancedQuery = enhancedQuery.eq('client_id', filters.clientId)
        }

        if (filters.projectId && filters.projectId !== 'all') {
          enhancedQuery = enhancedQuery.eq('project_id', filters.projectId)
        }

        if (filters.userId && filters.userId !== 'all' && userRole !== 'user') {
          enhancedQuery = enhancedQuery.eq('user_id', filters.userId)
        }

        // Apply date range filter
        if (filters.dateRange && filters.dateRange !== 'all') {
          const today = new Date()
          let startDate

          switch (filters.dateRange) {
            case 'today':
              startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate())
              enhancedQuery = enhancedQuery.gte('entry_date', startDate.toISOString().split('T')[0])
              break
            case 'week':
              const startOfWeek = new Date(today)
              startOfWeek.setDate(today.getDate() - today.getDay())
              enhancedQuery = enhancedQuery.gte('entry_date', startOfWeek.toISOString().split('T')[0])
              break
            case 'month':
              startDate = new Date(today.getFullYear(), today.getMonth(), 1)
              enhancedQuery = enhancedQuery.gte('entry_date', startDate.toISOString().split('T')[0])
              break
            case 'quarter':
              const quarter = Math.floor(today.getMonth() / 3)
              startDate = new Date(today.getFullYear(), quarter * 3, 1)
              enhancedQuery = enhancedQuery.gte('entry_date', startDate.toISOString().split('T')[0])
              break
          }
        }

        // Apply search query
        if (filters.search) {
          enhancedQuery = enhancedQuery.or(`task_name.ilike.%${filters.search}%,description.ilike.%${filters.search}%`)
        }

        // Apply sorting
        const sortBy = filters.sortBy || 'entry_date'
        const sortOrder = filters.sortOrder || 'desc'
        enhancedQuery = enhancedQuery.order(sortBy, { ascending: sortOrder === 'asc' })

        // Apply pagination
        if (filters.limit) {
          enhancedQuery = enhancedQuery.limit(filters.limit)
        }
        if (filters.offset) {
          enhancedQuery = enhancedQuery.range(filters.offset, filters.offset + (filters.limit || 50) - 1)
        }

        const enhancedResult = await enhancedQuery
        data = enhancedResult.data
        fetchError = enhancedResult.error

        if (fetchError) {
          console.log('Enhanced view failed:', fetchError.message)
          throw new Error('Enhanced view not available')
        }

        // Get unique IDs for related data
        const clientIds = [...new Set(data.map(e => e.client_id).filter(Boolean))]
        const projectIds = [...new Set(data.map(e => e.project_id).filter(Boolean))]
        const userIds = [...new Set(data.map(e => e.user_id).filter(Boolean))]

        // Fetch related data in parallel - INCLUDING tenant_memberships for bill_rate_role
        const [clientsData, projectsData, usersData, memberships] = await Promise.all([
          clientIds.length > 0 ? supabase
            .from('clients')
            .select('id, name, color')
            .in('id', clientIds)
            .abortSignal(signal)
            .then(({ data }) => data || []) : [],
          projectIds.length > 0 ? supabase
            .from('projects')
            .select('id, name, color, hourly_rate')
            .in('id', projectIds)
            .abortSignal(signal)
            .then(({ data }) => data || []) : [],
          userIds.length > 0 ? supabase
            .from('user_profiles')
            .select('id, full_name, email')
            .in('id', userIds)
            .abortSignal(signal)
            .then(({ data }) => data || []) : [],
          userIds.length > 0 ? supabase
            .from('tenant_memberships')
            .select('user_id, bill_rate_role')
            .eq('tenant_id', currentTenant.id)
            .in('user_id', userIds)
            .eq('status', 'active')
            .abortSignal(signal)
            .then(({ data }) => data || []) : []
        ])

        // Create lookup maps
        const clientsMap = new Map(clientsData.map(c => [c.id, c]))
        const projectsMap = new Map(projectsData.map(p => [p.id, p]))
        const usersMap = new Map(usersData.map(u => [u.id, u]))
        const billRateMap = new Map(memberships.map(m => [m.user_id, m.bill_rate_role]))

        // Transform enhanced data
        const transformedData = data.map(entry => {
          const client = clientsMap.get(entry.client_id) || { name: 'Unknown Client', color: '#6B7280' }
          const project = projectsMap.get(entry.project_id) || { name: 'Unknown Project', color: '#6B7280' }
          const userProfile = usersMap.get(entry.user_id) || { full_name: 'Unknown User', email: '' }

          // Parse splits if available
          let splits = null
          if (entry.is_split && entry.splits) {
            try {
              splits = typeof entry.splits === 'string' ? JSON.parse(entry.splits) : entry.splits
            } catch (parseError) {
              console.warn('Failed to parse splits for entry:', entry.id, parseError)
              splits = null
            }
          }

          return {
            ...entry,
            client_name: client.name,
            project_name: project.name,
            project_color: project.color,
            user_name: userProfile.full_name || userProfile.email,
            bill_rate_role: billRateMap.get(entry.user_id) || null,
            splits: splits,
            calculated_revenue: entry.calculated_revenue || 0,
            non_billable_hours: entry.non_billable_hours || 0,
            has_mixed_billing: entry.is_split && splits && splits.some(s => s.is_billable) && splits.some(s => !s.is_billable),
            billable_split_count: entry.is_split && splits ? splits.filter(s => s.is_billable).length : 0,
            non_billable_split_count: entry.is_split && splits ? splits.filter(s => !s.is_billable).length : 0,
            comments: entry.comments || [],
            tags: entry.tags || [],
            priority: entry.priority || 'medium'
          }
        })

        console.log(`Enhanced query successful: ${transformedData.length} entries (${transformedData.filter(e => e.is_split).length} split)`)
        setTimeEntries(transformedData)

      } catch (enhancedError) {
        if (enhancedError.name === 'AbortError') {
          console.log('Enhanced query was aborted')
          return
        }

        console.log('Enhanced query failed, using fallback:', enhancedError.message)
        
        // Fallback to regular time_entries table
        let fallbackQuery = supabase
          .from('time_entries')
          .select(`
            *,
            clients!inner(id, name, color),
            projects!inner(id, name, color, hourly_rate),
            user_profiles!inner(id, full_name, email)
          `)
          .eq('tenant_id', currentTenant.id)
          .abortSignal(signal)

        // Apply same filters to fallback
        if (userRole === 'user') {
          fallbackQuery = fallbackQuery.eq('user_id', user.id)
        }

        if (filters.status && filters.status !== 'all') {
          if (filters.status === 'not_approved') {
            // Handle "Pending Review" filter - includes submitted and in_review
            fallbackQuery = fallbackQuery.in('status', ['submitted', 'in_review'])
          } else {
            fallbackQuery = fallbackQuery.eq('status', filters.status)
          }
        }

        if (filters.clientId && filters.clientId !== 'all') {
          fallbackQuery = fallbackQuery.eq('client_id', filters.clientId)
        }

        if (filters.projectId && filters.projectId !== 'all') {
          fallbackQuery = fallbackQuery.eq('project_id', filters.projectId)
        }

        if (filters.userId && filters.userId !== 'all' && userRole !== 'user') {
          fallbackQuery = fallbackQuery.eq('user_id', filters.userId)
        }

        if (filters.search) {
          fallbackQuery = fallbackQuery.or(`task_name.ilike.%${filters.search}%,description.ilike.%${filters.search}%`)
        }

        const sortBy = filters.sortBy || 'entry_date'
        const sortOrder = filters.sortOrder || 'desc'
        fallbackQuery = fallbackQuery.order(sortBy, { ascending: sortOrder === 'asc' })

        if (filters.limit) {
          fallbackQuery = fallbackQuery.limit(filters.limit)
        }

        const fallbackResult = await fallbackQuery
        const fallbackData = fallbackResult.data
        const fallbackError = fallbackResult.error

        if (fallbackError) {
          throw fallbackError
        }

        // Fetch bill_rate_role for fallback as well
        const userIds = [...new Set(fallbackData.map(e => e.user_id).filter(Boolean))]
        const { data: memberships } = await supabase
          .from('tenant_memberships')
          .select('user_id, bill_rate_role')
          .eq('tenant_id', currentTenant.id)
          .in('user_id', userIds)
          .eq('status', 'active')
          .abortSignal(signal)

        const billRateMap = new Map((memberships || []).map(m => [m.user_id, m.bill_rate_role]))

        // Transform fallback data
        const transformedFallbackData = (fallbackData || []).map(entry => ({
          ...entry,
          client_name: entry.clients?.name || 'Unknown Client',
          project_name: entry.projects?.name || 'Unknown Project',
          project_color: entry.projects?.color || '#6B7280',
          user_name: entry.user_profiles?.full_name || entry.user_profiles?.email || 'Unknown User',
          bill_rate_role: billRateMap.get(entry.user_id) || null,
          splits: null,
          calculated_revenue: entry.is_billable ? (entry.duration_hours * (entry.hourly_rate || 0)) : 0,
          non_billable_hours: !entry.is_billable ? entry.duration_hours : 0,
          has_mixed_billing: false,
          billable_split_count: 0,
          non_billable_split_count: 0,
          comments: entry.comments || [],
          tags: entry.tags || [],
          priority: entry.priority || 'medium'
        }))

        console.log(`Fallback query successful: ${transformedFallbackData.length} entries`)
        setTimeEntries(transformedFallbackData)
      }
      
    } catch (err) {
      if (err.name === 'AbortError') {
        console.log('Fetch was aborted')
        return
      }
      
      console.error('All queries failed:', err)
      setError(err.message)
      setTimeEntries([])
      
    } finally {
      setLoading(false)
      isFetchingRef.current = false
      abortControllerRef.current = null
    }
  }

  // Debounced refetch function
  const refetch = () => {
    if (!isFetchingRef.current) {
      setTimeout(() => {
        lastFiltersRef.current = null // Force refetch
        fetchTimeEntries()
      }, 100)
    }
  }

  // CRUD operations

  const createTimeEntry = async (entryData) => {
    try {
      const { data, error } = await supabase
        .from('time_entries')
        .insert({
          ...entryData,
          tenant_id: currentTenant.id,
          user_id: user.id,
          is_split: false,
          parent_entry_id: null,
          split_by: null,
          split_at: null
        })
        .select()
        .single()

      if (error) throw error

      refetch()
      return { data, error: null }
    } catch (error) {
      console.error('Error creating time entry:', error)
      return { data: null, error: error.message }
    }
  }

  const updateTimeEntry = async (entryId, updates) => {
    try {
      const { data, error } = await supabase
        .from('time_entries')
        .update(updates)
        .eq('id', entryId)
        .select()
        .single()

      if (error) throw error

      refetch()
      return { data, error: null }
    } catch (error) {
      console.error('Error updating time entry:', error)
      return { data: null, error: error.message }
    }
  }

  const deleteTimeEntry = async (entryId) => {
    try {
      const { error } = await supabase
        .from('time_entries')
        .delete()
        .eq('id', entryId)

      if (error) throw error

      refetch()
      return { error: null }
    } catch (error) {
      console.error('Error deleting time entry:', error)
      return { error: error.message }
    }
  }

  const splitTimeEntry = async (entryId, splits) => {
    try {
      console.log('Splitting time entry:', entryId)
      console.log('Splits:', splits)
      
      const { data, error } = await supabase.rpc('apply_time_entry_split', {
        p_entry_id: entryId,
        p_splits: splits,
        p_split_by: user.id
      })

      if (error) throw error

      console.log('Split applied successfully:', data)
      refetch()
      
      return { data, error: null }
    } catch (error) {
      console.error('Error splitting time entry:', error)
      return { data: null, error: error.message }
    }
  }

  const revertSplit = async (entryId) => {
    try {
      console.log('Reverting split for entry:', entryId)
      
      const { data, error } = await supabase.rpc('revert_time_entry_split', {
        p_entry_id: entryId
      })

      if (error) throw error

      console.log('Split reverted successfully:', data)
      refetch()
      
      return { data, error: null }
    } catch (error) {
      console.error('Error reverting split:', error)
      return { data: null, error: error.message }
    }
  }

  // Enhanced analytics function
  const getSummaryStats = () => {
    const stats = {
      totalEntries: timeEntries.length,
      totalHours: 0,
      billableHours: 0,
      nonBillableHours: 0,
      totalRevenue: 0,
      splitEntries: 0,
      mixedBillingEntries: 0,
      pendingEntries: 0,
      approvedEntries: 0,
      draftEntries: 0,
      averageHourlyRate: 0
    }

    let totalBillableAmount = 0
    let totalBillableTime = 0

    timeEntries.forEach(entry => {
      stats.totalHours += entry.duration_hours || 0
      stats.totalRevenue += entry.calculated_revenue || 0
      
      if (entry.is_split) {
        stats.splitEntries++
        if (entry.has_mixed_billing) {
          stats.mixedBillingEntries++
        }
        stats.billableHours += (entry.duration_hours || 0) - (entry.non_billable_hours || 0)
        stats.nonBillableHours += entry.non_billable_hours || 0
        
        const billableTime = (entry.duration_hours || 0) - (entry.non_billable_hours || 0)
        if (billableTime > 0) {
          totalBillableTime += billableTime
          totalBillableAmount += entry.calculated_revenue || 0
        }
      } else {
        if (entry.is_billable) {
          stats.billableHours += entry.duration_hours || 0
          totalBillableTime += entry.duration_hours || 0
          totalBillableAmount += entry.calculated_revenue || 0
        } else {
          stats.nonBillableHours += entry.duration_hours || 0
        }
      }

      if (entry.status === 'submitted' || entry.status === 'in_review') {
        stats.pendingEntries++
      } else if (entry.status === 'approved') {
        stats.approvedEntries++
      } else if (entry.status === 'draft') {
        stats.draftEntries++
      }
    })

    stats.averageHourlyRate = totalBillableTime > 0 ? totalBillableAmount / totalBillableTime : 0

    return stats
  }

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [])

  return {
    timeEntries,
    loading,
    error,
    refetch,
    createTimeEntry,
    updateTimeEntry,
    deleteTimeEntry,
    splitTimeEntry,
    revertSplit,
    getSummaryStats
  }
}